import { useCallback, useEffect, useState } from 'react';
import { Fingerprint } from 'lucide-react';
import {
  addPasskey,
  listPasskeys,
  passkeysSupported,
  removePasskey,
  type AddPasskeyOutcome,
  type PasskeyListOutcome,
  type PasskeyRemoveOutcome,
} from '../../lib/api/passkeys';
import { useLocale, useT } from '../../i18n';

// ─────────────────────────────────────────────────────────────────────
// ADDING A PASSKEY, from account settings (#680, #675).
//
// REGISTRATION BEFORE SIGN-IN, deliberately. The sign-in screen's «sign in with
// passkey» button is a separate slice, and shipping it first would put a control
// on the login screen that nobody on earth could use — nobody has a credential
// until this exists. This way round the feature is useful the day it lands and
// the login button is useful the day it follows.
//
// THE SECTION HIDES ITSELF when the browser cannot do WebAuthn. An offer that
// cannot be honoured is worse than no offer — the same rule `LoginScreen` follows
// for recovery and for the e-mail code, both of which render nothing rather than
// a dead link.
//
// THE LIST IS HERE NOW (#675 slice 10). Until it was, a person could accumulate
// credentials they could not see, and «is this device already registered?» could
// only be answered by trying to add one again and reading the refusal.
//
// REMOVING IS HERE NOW, and WITHOUT A STEP-UP — decided 2026-10-08. The session
// is already VERIFIED, and removing a credential cannot lock anybody out while
// the e-mail code exists, so a second proof would re-ask a question just
// answered. The confirm below is about SLIPS, not about identity.
// ─────────────────────────────────────────────────────────────────────

type State = { step: 'idle' } | { step: 'running' } | { step: 'done'; outcome: AddPasskeyOutcome };

export function PasskeySection() {
  const t = useT();
  const { formatDate } = useLocale();
  const [label, setLabel] = useState('');
  const [state, setState] = useState<State>({ step: 'idle' });
  const [list, setList] = useState<PasskeyListOutcome | null>(null);
  /** Which row is asking «fjern denne?», and which is mid-flight. */
  const [confirming, setConfirming] = useState<string | null>(null);
  const [removingId, setRemovingId] = useState<string | null>(null);
  const [removed, setRemoved] = useState<PasskeyRemoveOutcome | null>(null);

  // Computed once at render rather than stored: a browser does not gain WebAuthn
  // mid-session, and a `useState` initialiser would be one more thing to keep in
  // step with a prop nobody has.
  const supported = passkeysSupported();

  // Re-read after every successful add, so the row somebody just created is
  // there rather than appearing on the next visit. The server owns the list;
  // nothing is spliced in locally from the add's reply.
  const refresh = useCallback(() => {
    // ── THE GUARD BELONGS IN THE EFFECT, NOT ONLY IN THE RENDER ────
    //
    // An early `return null` happens AFTER the hooks have run, so a section that
    // renders nothing still runs its effects. Without this, a browser with no
    // WebAuthn — which is every jsdom test, and every older browser — asked the
    // server for a list it was never going to show. The account surface has an
    // invariant that it fetches nothing just to render, and this broke it.
    if (!passkeysSupported()) return;
    void listPasskeys().then(setList);
  }, []);
  useEffect(refresh, [refresh]);

  if (!supported) return null;
  // The SAME rule, one layer out: a session that did not authenticate locally
  // cannot hold a passkey, so there is nothing here to offer it either.
  if (list?.status === 'unavailable') return null;

  const busy = state.step === 'running';

  const run = async () => {
    if (busy) return;
    setState({ step: 'running' });
    const outcome = await addPasskey(label.trim() || undefined);
    setState({ step: 'done', outcome });
    if (outcome.status === 'added') {
      setLabel('');
      refresh();
    }
  };

  const remove = async (id: string) => {
    if (removingId) return;
    setConfirming(null);
    setRemovingId(id);
    const outcome = await removePasskey(id);
    setRemovingId(null);
    setRemoved(outcome);
    // RE-READ ON «gone» TOO. «It was not there» and «it was not yours» arrive as
    // one answer, and in both cases what the screen is showing is out of date —
    // which is the one thing a refresh fixes and an error message does not.
    if (outcome.status !== 'unreachable') refresh();
  };

  const removeMessage = (outcome: PasskeyRemoveOutcome): { text: string; tone: 'good' | 'bad' } => {
    switch (outcome.status) {
      case 'removed':
        return { text: t('account.passkeys.removed'), tone: 'good' };
      case 'gone':
        // Not red: the list was stale, the end state is the one they wanted.
        return { text: t('account.passkeys.removeGone'), tone: 'good' };
      case 'reauthentication_required':
        // The session is live; its fifteen-minute window has lapsed (#1122).
        // The step-up prompt opens from the transport layer; this sentence says
        // why the row is still there.
        return { text: t('account.passkeys.stepUp'), tone: 'bad' };
      default:
        return { text: t('account.passkeys.removeFailed'), tone: 'bad' };
    }
  };

  const message = (outcome: AddPasskeyOutcome): { text: string; tone: 'good' | 'bad' } => {
    switch (outcome.status) {
      case 'added':
        // ── A SINGLE-DEVICE CREDENTIAL IS WORTH SAYING OUT LOUD ────
        //
        // `backedUp: false` means the passkey lives on this device only and dies
        // with it. Somebody relying on it as their only way in deserves to know
        // that BEFORE they need it, and the server already tells us.
        return {
          text: outcome.backedUp
            ? t('account.passkeys.added')
            : t('account.passkeys.addedNotBackedUp'),
          tone: 'good',
        };
      case 'already_registered':
        // ── NOT «nothing was added» (#694 review) ─────────────────
        //
        // The authenticator refused because `excludeCredentials` — scoped to this
        // identity — already lists one of its credentials. So this device DOES
        // have a passkey for this account, and saying «no passkey was added»
        // sends the person looking for a problem that does not exist.
        return { text: t('account.passkeys.alreadyRegistered'), tone: 'good' };
      case 'cancelled':
        // Not red. Dismissing the prompt is a choice, and «something went wrong»
        // tells somebody their device is broken when they changed their mind.
        return { text: t('account.passkeys.cancelled'), tone: 'good' };
      case 'unsupported':
        return { text: t('account.passkeys.unsupported'), tone: 'bad' };
      case 'not_configured':
        return { text: t('errors.not_configured'), tone: 'bad' };
      case 'unreachable':
        return { text: t('account.passkeys.network'), tone: 'bad' };
      case 'reauthentication_required':
        // Adding a passkey is high-impact since #1122: a stale session is told
        // to prove itself first, and the step-up prompt opens from the
        // transport layer. Said here too, so «nothing happened» has a reason.
        return { text: t('account.passkeys.stepUp'), tone: 'bad' };
      default:
        return { text: t('account.passkeys.failed'), tone: 'bad' };
    }
  };

  const shown = state.step === 'done' ? message(state.outcome) : null;

  return (
    <section className="card p-5">
      <h2 className="font-display text-lg font-bold text-ink inline-flex items-center gap-2">
        <Fingerprint size={18} /> {t('account.passkeys')}
      </h2>
      <p className="mt-2 text-sm text-ink-2">{t('account.passkeys.lead')}</p>

      {/* ── WHAT YOU ALREADY HAVE ──────────────────────────────────── */}
      <div className="mt-4">
        {list === null && <p className="text-sm text-ink-3">{t('account.passkeys.listLoading')}</p>}
        {list?.status === 'unreachable' && (
          <p className="text-sm text-danger">{t('account.passkeys.listFailed')}</p>
        )}
        {list?.status === 'ok' && list.passkeys.length === 0 && (
          <p className="text-sm text-ink-3">{t('account.passkeys.listEmpty')}</p>
        )}
        {removed && (
          <p
            role="status"
            aria-live="polite"
            className={`mt-2 text-sm ${
              removeMessage(removed).tone === 'good' ? 'text-ink-2' : 'text-danger'
            }`}
          >
            {removeMessage(removed).text}
          </p>
        )}
        {list?.status === 'ok' && list.passkeys.length > 0 && (
          <ul className="flex flex-col gap-2">
            {list.passkeys.map((pk) => (
              <li
                key={pk.id}
                className="rounded-lg border border-line-soft px-3 py-2 flex flex-col gap-0.5"
              >
                <span className="text-sm font-semibold text-ink">
                  {pk.label ?? t('account.passkeys.unnamed')}
                </span>
                <span className="text-xs text-ink-3">
                  {/* DISPLAY ONLY — `formatDate` never produces a key. The row
                      carries an ISO instant and that is what is compared. */}
                  {t('account.passkeys.addedOn', { date: formatDate(new Date(pk.createdAt)) })}
                  {' · '}
                  {pk.lastUsedAt
                    ? t('account.passkeys.lastUsed', {
                        date: formatDate(new Date(pk.lastUsedAt)),
                      })
                    : t('account.passkeys.neverUsed')}
                </span>

                {/* ── CONFIRM IN PLACE, NEVER `window.confirm()` ──────
                    A browser dialog is forbidden here (DOMAIN_RULES), and it
                    would be the wrong shape anyway: the question is about THIS
                    row, so it belongs on this row. */}
                <div className="mt-1 flex items-center gap-2">
                  {confirming === pk.id ? (
                    <>
                      <span className="text-xs text-ink-2">
                        {t('account.passkeys.removeConfirm')}
                      </span>
                      <button
                        type="button"
                        className="text-xs font-semibold text-danger underline"
                        disabled={removingId !== null}
                        onClick={() => void remove(pk.id)}
                      >
                        {removingId === pk.id
                          ? t('account.passkeys.removing')
                          : t('account.passkeys.removeYes')}
                      </button>
                      <button
                        type="button"
                        className="text-xs text-ink-3 underline"
                        onClick={() => setConfirming(null)}
                      >
                        {t('account.passkeys.removeNo')}
                      </button>
                    </>
                  ) : (
                    <button
                      type="button"
                      className="text-xs text-ink-3 underline"
                      disabled={removingId !== null}
                      onClick={() => {
                        setRemoved(null);
                        setConfirming(pk.id);
                      }}
                    >
                      {t('account.passkeys.remove')}
                    </button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="mt-4 flex flex-col gap-3">
        <label className="flex flex-col gap-1">
          <span className="text-xs font-semibold text-ink-3">
            {t('account.passkeys.labelLabel')}
          </span>
          <input
            className="input"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder={t('account.passkeys.labelPlaceholder')}
            maxLength={64}
            disabled={busy}
          />
        </label>

        <div className="flex items-center gap-3 flex-wrap">
          <button type="button" className="btn-lime" onClick={() => void run()} disabled={busy}>
            {busy ? t('account.passkeys.adding') : t('account.passkeys.add')}
          </button>
          {shown && (
            <span
              role="status"
              aria-live="polite"
              className={`text-sm ${shown.tone === 'good' ? 'text-ink-2' : 'text-danger'}`}
            >
              {shown.text}
            </span>
          )}
        </div>
      </div>
    </section>
  );
}
